import React, { useState } from 'react';

import { Paper } from '@mui/material';
import { SnackbarProvider } from 'notistack';
import { useParams } from 'react-router-dom';
import { AlineButton, BaseSnackbar } from 'src/components';
import { AlineLogo } from 'src/components/icons/AlineLogo';
import { WorkflowLaunchConfigBackground } from 'src/components/icons/WorkflowLaunchConfigBackground';
import { homePageRoute } from 'src/config';
import { openLink } from 'src/helpers';
import { LaunchConfigFileStackProvider } from 'src/helpers/integrations';
import { defaultSnackBarDisplayTimeMS } from 'src/helpers/snackbars';
import AlineInfoImage from 'src/images/aline-info.png';
import type { ReactSetStateAction } from 'src/types';
import { makeStyles } from 'tss-react/mui';

import { LaunchConfigForm } from './LaunchConfigForm';
import { LAUNCH_FOOTER_HEIGHT } from './LaunchFooter';
import type { LaunchSuccessProps } from './LaunchSuccess';
import { LaunchSuccess } from './LaunchSuccess';

const useStyles = makeStyles()(theme => ({
  alineInfo: {
    alignItems: 'center',
    background: theme.palette.common.white,
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
    padding: theme.spacing(2, 3),
    width: 832,
  },
  alineInfoImage: {
    height: 282,
    width: 394,
  },
  container: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  img: {
    left: 0,
    objectFit: 'cover',
    position: 'absolute',
    top: 0,
    width: '100%',
  },
  main: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    gap: theme.spacing(4),
    // Extra room at the bottom so the pinned footer never covers the last section.
    padding: theme.spacing(6, 0, `${LAUNCH_FOOTER_HEIGHT + 48}px`),
  },
  root: {
    background: '#E8ECF2',
    boxSizing: 'border-box',
    display: 'flex',
    justifyContent: 'center',
    minHeight: '100vh',
    minWidth: theme.breakpoints.values.lg,
    overflow: 'hidden',
    position: 'relative',
  },
}));

export function LaunchPage() {
  const { classes } = useStyles();
  const { launchConfigId } = useParams<{ launchConfigId: UUID }>();

  return (
    <SnackbarProvider
      Components={{
        default: BaseSnackbar,
        error: BaseSnackbar,
        info: BaseSnackbar,
        success: BaseSnackbar,
        warning: BaseSnackbar,
      }}
      autoHideDuration={defaultSnackBarDisplayTimeMS}
    >
      <div className={classes.root}>
        <WorkflowLaunchConfigBackground className={classes.img} />
        {launchConfigId && (
          <LaunchConfigFileStackProvider launchConfigId={launchConfigId}>
            <LaunchPageBody launchConfigId={launchConfigId} />
          </LaunchConfigFileStackProvider>
        )}
      </div>
    </SnackbarProvider>
  );
}

function LaunchPageBody({ launchConfigId }: { launchConfigId: UUID }) {
  const { classes } = useStyles();

  return (
    <div className={classes.container}>
      <Paper className={classes.alineInfo}>
        <AlineLogo />
        <AlineButton color="primary" variant="contained" onClick={() => openLink(homePageRoute)}>
          Learn About Aline
        </AlineButton>
      </Paper>
      <div className={classes.main}>
        <LaunchConfigBody launchConfigId={launchConfigId} />
      </div>
    </div>
  );
}

function LaunchConfigBody({ launchConfigId }: { launchConfigId: UUID }) {
  const [successProps, setSuccessProps] = useState<LaunchSuccessProps | null>(null);

  if (successProps) return <LaunchSuccess {...successProps} />;

  return <LaunchConfigFill launchConfigId={launchConfigId} setSuccessProps={setSuccessProps} />;
}

export function LaunchConfigFill({
  launchConfigId,
  setSuccessProps,
}: {
  launchConfigId: UUID;
  setSuccessProps: ReactSetStateAction<LaunchSuccessProps | null>;
}) {
  const { classes } = useStyles();

  return (
    <>
      <img className={classes.alineInfoImage} src={AlineInfoImage} srcSet={AlineInfoImage} />
      <LaunchConfigForm
        afterSubmit={launchedWorkflow =>
          setSuccessProps({
            launchedWorkflow,
          })
        }
        launchConfigId={launchConfigId}
      />
    </>
  );
}
